// src/layouts/AdminLayout.jsx
"use client";

import React, { useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Button,
  Stack,
  Paper,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
} from "@mui/material";
import {
  Dashboard,
  People,
  CreditCard,
  SwapHoriz,
  VerifiedUser,
  MonetizationOn,
  Settings,
  History,
  NotificationsNone as NotificationsIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  ArrowBack,
  Brightness2 as DarkIcon,
  LightMode as LightIcon,
  LockReset as LockResetIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, Outlet, Link as RouterLink } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useThemeContext } from "../../pages/context/ThemeContext";
import { format, isToday, isYesterday } from "date-fns";
import { useAuth } from "../../contexts/AuthContext";
import { getAdminProfile, updateAdminProfile, changeAdminPassword } from "../../utils/api";

function SidebarIcon({ icon, label, to, active, onClick, tooltip }) {
  return (
    <Tooltip title={tooltip || label} placement="right">
      <motion.div
        whileHover={{ scale: 1.05 }}
        style={{ marginBottom: 16, cursor: onClick || to ? "pointer" : "default" }}
        onClick={onClick}
      >
        <Button
          component={to ? RouterLink : "button"}
          to={to}
          startIcon={icon}
          fullWidth
          sx={{
            justifyContent: "flex-start",
            bgcolor: active ? "#232e41" : "transparent",
            color: active ? "#5C6AC4" : "#cbd5e1",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.8rem",
            px: 2,
            py: 1,
            borderRadius: 2,
            "&:hover": { bgcolor: "#232e41", color: "#9B51E0" },
          }}
        >
          {label}
        </Button>
      </motion.div>
    </Tooltip>
  );
}

function AdminSidebar({ open, onClose, onLogout }) {
  const { mode, toggleTheme } = useThemeContext();
  const location = useLocation();
  const menuItems = [
    { text: "Dashboard", icon: <Dashboard />, path: "/admin/dashboard" },
    { text: "Users", icon: <People />, path: "/admin/users" },
    { text: "Cards", icon: <CreditCard />, path: "/admin/cards" },
    { text: "Transactions", icon: <SwapHoriz />, path: "/admin/transactions" },
    { text: "KYC", icon: <VerifiedUser />, path: "/admin/kyc" },
    { text: "Currencies", icon: <MonetizationOn />, path: "/admin/currencies" },
    { text: "Payment Codes", icon: <Settings />, path: "/admin/codes" },
  ];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ x: "-100%" }}
          animate={{ x: 0 }}
          exit={{ x: "-100%" }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: 240,
            height: "100vh",
            background: "#0D0F2B",
            zIndex: 2000,
            boxShadow: "2px 0 16px #0008",
            pointerEvents: open ? "auto" : "none",
            overflowY: "auto",
          }}
        >
          <Box p={3}>
            <Typography fontWeight="bold" color="#fff" fontSize={22}>⚡</Typography>
            <Typography variant="caption" color="#5C6AC4" fontWeight="bold" ml={2}>
              ADMIN PANEL
            </Typography>
            <Stack spacing={2} mt={3}>
              {menuItems.map((item) => (
                <SidebarIcon
                  key={item.text}
                  icon={item.icon}
                  label={item.text}
                  to={item.path}
                  active={location.pathname === item.path}
                  onClick={onClose}
                />
              ))}
              <SidebarIcon
                icon={mode === "dark" ? <LightIcon /> : <DarkIcon />}
                label={mode === "dark" ? "Light Mode" : "Dark Mode"}
                onClick={toggleTheme}
              />
              <SidebarIcon icon={<LogoutIcon />} label="Logout" onClick={onLogout} />
            </Stack>
            <Button
              onClick={onClose}
              sx={{ mt: 4, color: "#fff", border: "1px solid #fff", borderRadius: 2, width: "100%" }}
            >
              Close
            </Button>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function AdminLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const { logout, user, setUser } = useAuth();
  const { mode } = useThemeContext();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  // separate loading flags
  const [profileLoading, setProfileLoading] = useState(false); // fetching profile
  const [profileSaving, setProfileSaving] = useState(false);   // saving profile
  const [passwordSaving, setPasswordSaving] = useState(false); // saving password

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const open = Boolean(anchorEl);

  const [notifications, setNotifications] = useState([
    { id: 1, text: "New user registered", time: new Date(), read: false },
    { id: 2, text: "Transaction #102 completed", time: new Date(Date.now() - 3600 * 1000), read: false },
    { id: 3, text: "KYC approved for John Doe", time: new Date(Date.now() - 86400 * 1000), read: true },
    { id: 4, text: "Server rebooted", time: new Date(Date.now() - 2 * 86400 * 1000), read: false },
  ]);

  const markAsRead = (id) =>
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));

  const groupedNotifications = {
    Today: notifications.filter((n) => isToday(n.time)),
    Yesterday: notifications.filter((n) => isYesterday(n.time)),
    Older: notifications.filter((n) => !isToday(n.time) && !isYesterday(n.time)),
  };

  /* ====================== Profile Modal (FULL-PAGE ADMIN STYLE) ====================== */
  const [profileForm, setProfileForm] = useState({ name: "", email: "" });

  // open modal immediately, fetch data in background (gives snappier UI)
  const openProfileModal = () => {
    setProfileOpen(true);
    setProfileLoading(true);
    getAdminProfile()
      .then((res) => {
        const payload = res?.data || {};
        setProfileForm({
          name: payload.name || "",
          email: payload.email || "",
        });
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || "Failed to fetch profile";
        setSnackbar({ open: true, message: msg, severity: "error" });
      })
      .finally(() => setProfileLoading(false));
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      const res = await updateAdminProfile(user.id, profileForm);
      const payload = res?.data || {};
      // backend returns { message, user }
      if (payload.user) {
        setUser(payload.user); // update auth context (use whatever setUser is in your AuthContext)
      } else {
        // fallback: merge
        setUser({ ...user, ...profileForm });
      }
      setProfileOpen(false);
      setSnackbar({ open: true, message: payload.message || "Profile updated successfully", severity: "success" });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Error updating profile";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setProfileSaving(false);
    }
  };

  /* ====================== Password Modal (Small, upgraded design) ====================== */
  const [passwordForm, setPasswordForm] = useState({ old_password: "", new_password: "", confirm_password: "" });

  const savePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setSnackbar({ open: true, message: "Passwords do not match", severity: "error" });
      return;
    }
    setPasswordSaving(true);
    try {
      const res = await changeAdminPassword(user.id, {
        old_password: passwordForm.old_password,
        new_password: passwordForm.new_password,
      });
      const payload = res?.data || {};
      setPasswordForm({ old_password: "", new_password: "", confirm_password: "" });
      setPasswordOpen(false);
      setSnackbar({ open: true, message: payload.message || "Password updated successfully", severity: "success" });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Error changing password";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: 64,
          px: 2,
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          boxShadow: mode === "dark" ? "0 1px 4px rgba(255,255,255,0.05)" : "0 2px 8px rgba(0,0,0,0.05)",
          zIndex: 1000,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <motion.div whileTap={{ scale: 0.8, rotate: 90 }}>
            <IconButton onClick={() => setMenuOpen(true)}>
              <MenuIcon sx={{ color: theme.palette.text.primary }} />
            </IconButton>
          </motion.div>
          <Typography variant="h6" fontWeight="bold">
            Admin Dashboard
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={() => setNotifOpen(true)}>
            <NotificationsIcon sx={{ color: theme.palette.text.primary }} />
          </IconButton>

          <Tooltip title="Admin Menu">
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
              <Avatar sx={{ bgcolor: "#7B2FF2", width: 35, height: 35 }}>
                {user?.name?.charAt(0)?.toUpperCase() || "A"}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Avatar Menu */}
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <MenuItem
          onClick={() => {
            openProfileModal();
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          Update Profile
        </MenuItem>

        <MenuItem
          onClick={() => {
            setPasswordOpen(true);
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <LockResetIcon fontSize="small" />
          </ListItemIcon>
          Change Password
        </MenuItem>

        <Divider />
        <MenuItem
          onClick={() => {
            logout();
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Profile Modal (FULL-PAGE) */}
      <Dialog
        open={profileOpen}
        onClose={() => {
          setProfileOpen(false);
        }}
        fullScreen
      >
        <Box sx={{ p: 3, bgcolor: mode === "dark" ? "#0a0c1a" : "#f5f5f5", minHeight: "100vh" }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight="bold">Update Profile</Typography>
            <Button onClick={() => setProfileOpen(false)}>Close</Button>
          </Box>

          <Box sx={{ overflowY: "auto", maxHeight: "calc(100vh - 120px)", pr: { xs: 0, sm: 2 } }}>
            {profileLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 200 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, borderRadius: 3, bgcolor: mode === "dark" ? "#11142d" : "#fff" }}>
                    <TextField
                      label="Full Name"
                      fullWidth
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="Email Address"
                      fullWidth
                      value={profileForm.email}
                      onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                    />
                  </Paper>

                  <Stack direction="row" spacing={2} mt={3}>
                    <Button variant="outlined" onClick={() => setProfileOpen(false)} disabled={profileSaving}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={saveProfile}
                      disabled={profileSaving}
                      startIcon={profileSaving ? <CircularProgress color="inherit" size={18} /> : null}
                    >
                      {profileSaving ? "Saving..." : "Save Changes"}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            )}
          </Box>
        </Box>
      </Dialog>

      {/* Password Modal (Small) */}
      <Dialog open={passwordOpen} onClose={() => setPasswordOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Password</DialogTitle>

        <DialogContent dividers sx={{ px: 3 }}>
          <TextField
            margin="dense"
            label="Current Password"
            type="password"
            fullWidth
            value={passwordForm.old_password}
            onChange={(e) => setPasswordForm({ ...passwordForm, old_password: e.target.value })}
          />
          <TextField
            margin="dense"
            label="New Password"
            type="password"
            fullWidth
            value={passwordForm.new_password}
            onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Confirm New Password"
            type="password"
            fullWidth
            value={passwordForm.confirm_password}
            onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPasswordOpen(false)} disabled={passwordSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={savePassword}
            disabled={passwordSaving}
            startIcon={passwordSaving ? <CircularProgress color="inherit" size={18} /> : null}
          >
            {passwordSaving ? "Updating..." : "Update Password"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Sidebar */}
      <AdminSidebar open={menuOpen} onClose={() => setMenuOpen(false)} onLogout={logout} />

      {/* Notifications Panel */}
      <AnimatePresence>
        {notifOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: mode === "dark" ? "rgba(15,15,30,0.95)" : "#fff",
              zIndex: 2000,
              display: "flex",
              flexDirection: "column",
              pointerEvents: notifOpen ? "auto" : "none",
            }}
          >
            <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #3333" }}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Notifications
              </Typography>
              <IconButton onClick={() => setNotifOpen(false)}>
                <ArrowBack />
              </IconButton>
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: "auto", p: 2 }}>
              {Object.entries(groupedNotifications).map(([group, items]) =>
                items.length ? (
                  <Box key={group} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {group}
                    </Typography>
                    {items.map((n) => (
                      <Paper
                        key={n.id}
                        onClick={() => markAsRead(n.id)}
                        sx={{
                          p: 2,
                          my: 1,
                          bgcolor: n.read ? (mode === "dark" ? "rgba(20,20,40,0.5)" : "#f2f2f2") : (mode === "dark" ? "rgba(20,20,40,0.8)" : "#e0f7fa"),
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="body1">{n.text}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(n.time, "p, MMM d")}
                          </Typography>
                        </Box>
                        {!n.read && <Chip label="New" color="primary" size="small" />}
                      </Paper>
                    ))}
                  </Box>
                ) : null
              )}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          pt: 10,
          minHeight: "100vh",
          bgcolor: mode === "dark" ? "#0b0d1a" : "#f9f9f9",
          overflowY: "auto", // ensure page scroll works on mobile
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}